import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import {
  booklistAnswersResponseSchema,
  booklistSavedSchema,
  booklistSheetSchema,
  bookProjectResponseSchema,
  classCategoryListSchema,
  entrySheetSchema,
  requisitionSheetSavedSchema,
  requisitionSheetSchema,
  specimenRequisitionDecidedSchema,
  specimenRequisitionResponseSchema,
  spPromoRequisitionMembersSchema,
  spPromoRequisitionResponseSchema,
  studentCountReportSchema,
  studentCountsSavedSchema,
  studentProjectResponseSchema,
  studentProjectSchema,
  type BooklistAnswerLine,
  type BooklistAnswersResponse,
  type BooklistAnswersSearch,
  type BooklistSheet,
  type BooklistSheetSearch,
  type BookProjectResponse,
  type ClassCategory,
  type DecideRequisitionPayload,
  type EntrySheet,
  type EntrySheetSearch,
  type RequisitionReviewSearch,
  type RequisitionSheet,
  type RequisitionSheetSearch,
  type SaveStudentProjectPayload,
  type SpecimenRequisitionResponse,
  type SpPromoRequisitionSearch,
  type StudentCountLine,
  type StudentCountReport,
  type StudentProjectSearch,
} from '../types/productRequisition.types';

const PAGE_SIZE = 50;
const REPORT_PAGE_SIZE = 500;

export const requisitionQueryKeys = {
  all: () => ['product-requisition'] as const,
  sheet: (params: RequisitionSheetSearch) =>
    [...requisitionQueryKeys.all(), 'sheet', params] as const,
  review: (params: RequisitionReviewSearch) =>
    [...requisitionQueryKeys.all(), 'review', params] as const,
  promoReport: (params: SpPromoRequisitionSearch) =>
    [...requisitionQueryKeys.all(), 'promo-report', params] as const,
  promoMembers: (specimenId: number, params: SpPromoRequisitionSearch) =>
    [...requisitionQueryKeys.all(), 'promo-members', specimenId, params] as const,
  studentProjects: (params: StudentProjectSearch) =>
    [...requisitionQueryKeys.all(), 'student-projects', params] as const,
  classCategories: (instituteTypeId: number) =>
    [...requisitionQueryKeys.all(), 'class-categories', instituteTypeId] as const,
  entrySheet: (projectId: number, params: EntrySheetSearch) =>
    [...requisitionQueryKeys.all(), 'entry-sheet', projectId, params] as const,
  countReport: (projectId: number, params: EntrySheetSearch) =>
    [...requisitionQueryKeys.all(), 'count-report', projectId, params] as const,
  bookProjects: (params: StudentProjectSearch) =>
    [...requisitionQueryKeys.all(), 'book-projects', params] as const,
  booklistSheet: (projectId: number, params: BooklistSheetSearch) =>
    [...requisitionQueryKeys.all(), 'booklist-sheet', projectId, params] as const,
  booklistAnswers: (projectId: number, params: BooklistAnswersSearch) =>
    [...requisitionQueryKeys.all(), 'booklist-answers', projectId, params] as const,
};

/* ------------------------- 1. specimen requisition ------------------------- */

export const requisitionSheetQueryOptions = (params: RequisitionSheetSearch) =>
  queryOptions({
    queryKey: requisitionQueryKeys.sheet(params),
    queryFn: async (): Promise<RequisitionSheet> => {
      const res = await apiClient.get(API.product.specimenRequisitionSheet, { params });
      return requisitionSheetSchema.parse(res.data).data;
    },
    enabled: params.specimenId !== undefined,
    staleTime: STALE_TIME,
  });

export async function saveRequisitionSheet(
  specimenId: number,
  lines: {
    instituteId: number;
    requiredQty: number;
    teacherQty?: number;
    studentQty?: number;
    targetSaleQty?: number;
    targetDate?: string;
  }[],
) {
  const res = await apiClient.put(API.product.specimenRequisitionSheet, { specimenId, lines });
  return requisitionSheetSavedSchema.parse(res.data).data;
}

/* ---------------------- 2. specimen requisition review --------------------- */

export const requisitionReviewInfiniteQueryOptions = (params: RequisitionReviewSearch) =>
  infiniteQueryOptions({
    queryKey: requisitionQueryKeys.review(params),
    queryFn: async ({ pageParam }): Promise<SpecimenRequisitionResponse> => {
      const res = await apiClient.get(API.product.specimenRequisitions, {
        params: { ...params, page: pageParam, pageSize: PAGE_SIZE },
      });
      return specimenRequisitionResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function decideRequisition(id: number, payload: DecideRequisitionPayload) {
  const res = await apiClient.patch(API.product.specimenRequisitionDecision(id), payload);
  return specimenRequisitionDecidedSchema.parse(res.data).data;
}

/* ----------------------- 3. sp promotional requisition --------------------- */

export const spPromoRequisitionInfiniteQueryOptions = (params: SpPromoRequisitionSearch) =>
  infiniteQueryOptions({
    queryKey: requisitionQueryKeys.promoReport(params),
    queryFn: async ({ pageParam }) => {
      const res = await apiClient.get(API.product.spPromoRequisitionReport, {
        params: { ...params, page: pageParam, pageSize: PAGE_SIZE },
      });
      return spPromoRequisitionResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const spPromoRequisitionMembersQueryOptions = (
  specimenId: number,
  params: SpPromoRequisitionSearch,
) =>
  queryOptions({
    queryKey: requisitionQueryKeys.promoMembers(specimenId, params),
    queryFn: async () => {
      const res = await apiClient.get(API.product.spPromoRequisitionMembers, {
        params: { ...params, specimenId },
      });
      return spPromoRequisitionMembersSchema.parse(res.data).data;
    },
    staleTime: STALE_TIME,
  });

/* --------------------- 4. mkt student collection project -------------------- */

export const studentProjectsInfiniteQueryOptions = (params: StudentProjectSearch) =>
  infiniteQueryOptions({
    queryKey: requisitionQueryKeys.studentProjects(params),
    queryFn: async () => {
      const res = await apiClient.get(API.product.studentProjects, {
        params: { ...params, page: FIRST_PAGE, pageSize: PAGE_SIZE },
      });
      return studentProjectResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function createStudentProject(payload: SaveStudentProjectPayload) {
  const res = await apiClient.post(API.product.studentProjects, payload);
  return studentProjectSchema.parse((res.data as { data: unknown }).data);
}

export async function updateStudentProject(id: number, payload: SaveStudentProjectPayload) {
  const res = await apiClient.patch(API.product.studentProject(id), payload);
  return studentProjectSchema.parse((res.data as { data: unknown }).data);
}

export async function deleteStudentProject(id: number): Promise<void> {
  await apiClient.delete(API.product.studentProject(id));
}

export const classCategoriesQueryOptions = (instituteTypeId: number) =>
  queryOptions({
    queryKey: requisitionQueryKeys.classCategories(instituteTypeId),
    queryFn: async (): Promise<ClassCategory[]> => {
      const res = await apiClient.get(API.product.studentClassCategories(instituteTypeId));
      return classCategoryListSchema.parse(res.data).data;
    },
    staleTime: STALE_TIME,
  });

export const entrySheetQueryOptions = (projectId: number, params: EntrySheetSearch) =>
  queryOptions({
    queryKey: requisitionQueryKeys.entrySheet(projectId, params),
    queryFn: async (): Promise<EntrySheet> => {
      const res = await apiClient.get(API.product.studentEntrySheet(projectId), { params });
      return entrySheetSchema.parse(res.data).data;
    },
    enabled: params.instituteTypeId !== undefined,
    staleTime: STALE_TIME,
  });

export async function saveStudentCounts(projectId: number, counts: StudentCountLine[]) {
  const res = await apiClient.put(API.product.studentCounts(projectId), { counts });
  return studentCountsSavedSchema.parse(res.data).data;
}

export const studentCountReportQueryOptions = (projectId: number, params: EntrySheetSearch) =>
  queryOptions({
    queryKey: requisitionQueryKeys.countReport(projectId, params),
    queryFn: async (): Promise<StudentCountReport> => {
      const res = await apiClient.get(API.product.studentCountReport(projectId), {
        params: { ...params, page: FIRST_PAGE, pageSize: REPORT_PAGE_SIZE },
      });
      return studentCountReportSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

/* ------------------------ 5. booklisted institution ------------------------ */

export const bookProjectsInfiniteQueryOptions = (params: StudentProjectSearch) =>
  infiniteQueryOptions({
    queryKey: requisitionQueryKeys.bookProjects(params),
    queryFn: async (): Promise<BookProjectResponse> => {
      const res = await apiClient.get(API.product.bookSurveyProjects, {
        params: { ...params, page: FIRST_PAGE, pageSize: PAGE_SIZE },
      });
      return bookProjectResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const booklistSheetQueryOptions = (projectId: number, params: BooklistSheetSearch) =>
  queryOptions({
    queryKey: requisitionQueryKeys.booklistSheet(projectId, params),
    queryFn: async (): Promise<BooklistSheet> => {
      const res = await apiClient.get(API.product.booklistSheet(projectId), { params });
      return booklistSheetSchema.parse(res.data).data;
    },
    enabled: params.bookEditionId !== undefined,
    staleTime: STALE_TIME,
  });

export async function saveBooklistAnswers(
  projectId: number,
  bookEditionId: number,
  answers: BooklistAnswerLine[],
) {
  const res = await apiClient.put(API.product.booklistAnswers(projectId), {
    bookEditionId,
    answers,
  });
  return booklistSavedSchema.parse(res.data).data;
}

export const booklistAnswersInfiniteQueryOptions = (
  projectId: number,
  params: BooklistAnswersSearch,
) =>
  infiniteQueryOptions({
    queryKey: requisitionQueryKeys.booklistAnswers(projectId, params),
    queryFn: async ({ pageParam }): Promise<BooklistAnswersResponse> => {
      const res = await apiClient.get(API.product.booklistAnswers(projectId), {
        params: { ...params, page: pageParam, pageSize: PAGE_SIZE },
      });
      return booklistAnswersResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function decideBooklistAnswer(
  answerId: number,
  stage: 'REVIEW' | 'LEVEL_ONE' | 'LEVEL_TWO',
  accept: boolean,
) {
  await apiClient.patch(API.product.booklistDecision(answerId), { stage, accept });
}
